/**
 * Таблица — один компонент на всю админку.
 *
 * Смысл общего компонента не в экономии разметки, а в том, что состояний
 * у таблицы четыре, и раздел, написавший свою, обязательно забудет одно
 * из них. «Загружаю», «ошибка», «пусто» и «строки» выглядят по-разному, и
 * пустая таблица не должна быть неотличима от таблицы, которая не
 * загрузилась: первое — нормальная жизнь, второе — сломанный сервер.
 */

import type { ReactNode } from 'react'

export interface Column<Row> {
  key: string
  title: string
  /** Числа выравниваем вправо и печатаем табличными цифрами. */
  numeric?: boolean
  width?: string
  render: (row: Row) => ReactNode
}

export interface DataTableProps<Row> {
  columns: Column<Row>[]
  rows: Row[]
  rowKey: (row: Row) => string
  loading?: boolean
  error?: string | null
  /** Что показать вместо строк, когда их нет. */
  empty?: string
  onRowClick?: (row: Row) => void
}

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  loading = false,
  error = null,
  empty = 'Nothing here',
  onRowClick,
}: DataTableProps<Row>): JSX.Element {
  const state =
    error !== null
      ? { tone: 'error', text: error }
      : loading && rows.length === 0
        ? { tone: 'muted', text: 'Loading…' }
        : rows.length === 0
          ? { tone: 'muted', text: empty }
          : null

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={column.numeric === true ? 'num' : undefined}
                style={column.width === undefined ? undefined : { width: column.width }}
              >
                {column.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {state !== null ? (
            <tr>
              <td colSpan={columns.length}>
                <div
                  className="table__state"
                  style={state.tone === 'error' ? { color: 'var(--danger)' } : undefined}
                >
                  {state.text}
                </div>
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                className={onRowClick === undefined ? undefined : 'is-clickable'}
                onClick={onRowClick === undefined ? undefined : () => onRowClick(row)}
              >
                {columns.map((column) => (
                  <td key={column.key} className={column.numeric === true ? 'num' : undefined}>
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
